/**
 * ProductDetailPage - Fixed Version
 * Properly fetches product details from WooCommerce API
 */
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Package,
  Phone,
  Truck,
  Shield,
  Clock,
  ChevronLeft,
  ChevronRight,
  FileText,
  Settings,
  CreditCard,
  HomeIcon,
  Banknote,
  CalendarClock,
  Zap,
  CheckCircle,
  Calendar,
} from "lucide-react";
import LoadingSpinner from "../components/LoadingSpinner";
import RelatedProducts from "../components/RelatedProducts";
import EnquiryPopup from "../components/EnquiryPopup";
import ProductTextIconSlider from "../components/ProductTextIconSlider";
import { fetchProduct } from "../services/api";
import PaymentIcons from "../assets/images/Payment_Icons.png";
import BankTransfer from "../assets/images/bank_transfer.png";
import BuyNowPayLater from "../assets/images/buy_now_pay_later.png";
import WireTransfer from "../assets/images/wire_transfer.png";
import WhatsAppButton from "../components/WhatsAppButton";
import GetAQuoteEnquiryForm from "../components/GetAQuoteEnquiryForm";
const ProductDetailPage = () => {
  const { productId, productSlug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [activeSection, setActiveSection] = useState("technical");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
    const handleOpenPopup = () => {
      setIsPopupOpen(true);
    };
    const handleClosePopup = () => {
      setIsPopupOpen(false);
    };
  useEffect(() => {
    fetchProductDetails();
  }, [productId]);

  // Calculate header height dynamically
  useEffect(() => {
    const calculateHeaderHeight = () => {
      // Find the main header/navbar element (adjust selector to match your actual header)
      const header = document.querySelector('header') || document.querySelector('nav');
      const iconSlider = document.querySelector('.product-text-icon-slider');

      let totalHeight = 0;

      if (header) {
        totalHeight += header.offsetHeight;
      }

      if (iconSlider) {
        totalHeight += iconSlider.offsetHeight;
      }

      // Fallback to reasonable defaults if elements not found
      setHeaderHeight(totalHeight || 80);
    };

    calculateHeaderHeight();

    // Recalculate on window resize
    window.addEventListener('resize', calculateHeaderHeight);
    return () => window.removeEventListener('resize', calculateHeaderHeight);
  }, []);

  // Navigation items - filtered based on content availability using useMemo
  const navItems = useMemo(() => {
    if (!product) return [];

    // Function to check if a section has content
    const hasSectionContent = (sectionId) => {
      switch (sectionId) {
        case "technical":
          // Check if either technical_specification array or technical_specification_table exists
          return (
            (product.acf_fields?.technical_specification?.length > 0) ||
            (product.acf_fields?.technical_specification_table)
          );
        case "product_description":
          // Check if description exists and is not empty
          return product.description && product.description.trim() !== "";
        case "payment":
          // Payment section is always shown (static content)
          return true;
        case "related_products":
          // Check if related products exist
          return product && product.related_ids && product.related_ids.length > 0;
        default:
          return false;
      }
    };

    const allNavItems = [
      { id: "technical", label: "Technical Specifications", icon: Settings },
      { id: "product_description", label: "Description", icon: FileText },
      { id: "payment", label: "Payment Options", icon: CreditCard },
      { id: "related_products", label: "Related Products", icon: Package },
    ];

    return allNavItems.filter((item) => hasSectionContent(item.id));
  }, [product]);

  // Track scroll position to highlight active section
  useEffect(() => {
    if (!product || navItems.length === 0) return;

    const handleScroll = () => {
      // Only track sections that are actually visible in navigation
      const sections = navItems.map(item => item.id);
      const navHeight = headerHeight || 80;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section) {
          const sectionTop = section.offsetTop - navHeight - 80;
          if (window.scrollY >= sectionTop) {
            setActiveSection(sections[i]);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [headerHeight, navItems, product]);

  const fetchProductDetails = async () => {
    if (!productId) return;
    
    setLoading(true);
    setError(null);

    try {
      const product = await fetchProduct(productId);
      setProduct(product);
    } catch (err) {
      console.error("Failed to fetch product:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Decode HTML entities
  const decodeHTML = (html) => {
    if (!html) return "";
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" message="Loading product details..." />
      </div>
    );
  }

  if (error && !product) {
    return (
      <div id="productPage" className="productPage min-h-screen bg-neutral-50 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Package className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
          <h1 className="text-2xl font-display font-bold text-neutral-900 mb-2">
            Product Not Found
          </h1>
          <p className="text-neutral-600 mb-2">{error}</p>
          <p className="text-sm text-neutral-500 mb-6">
            Product ID: {productId} | Slug: {productSlug || 'N/A'}
          </p>
          <button
            onClick={() => navigate("/products")}
            className="inline-flex items-center px-6 py-3 bg-accent-600 text-white rounded-[4px] hover:bg-accent-700"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  // Process images
  const images = product.images || [];
  const mainImage = images[selectedImage] || images[0] || {};
  const imageUrl =
    mainImage.src || mainImage.url || "https://via.placeholder.com/600x400";

  // Thumbnail slider functions
  const imagesPerSlide = 4;
  const totalSlides = Math.ceil(images.length / imagesPerSlide);

  const nextSlide = () => {
    setCurrentSlideIndex((prev) => (prev < totalSlides - 1 ? prev + 1 : 0));
  };

  const prevSlide = () => {
    setCurrentSlideIndex((prev) => (prev > 0 ? prev - 1 : totalSlides - 1));
  };

  // Smooth scroll function
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      const navHeight = headerHeight + 80; // dynamic header height + section nav height
      const elementPosition = element.offsetTop - navHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
    }
  };

  // Build dynamic breadcrumb based on product data
  const buildBreadcrumbs = () => {
    const breadcrumbs = [
      { label: 'Home', href: '/', icon: HomeIcon },
      { label: 'Inventory', href: '/products' },
    ];

    // Add taxonomy-based breadcrumb (For Sale / For Rent)
    if (product.taxonomy === 'equipment-buy') {
      breadcrumbs.push({
        label: 'For Sale',
        href: '/products?taxonomy=equipment-buy'
      });
    } else if (product.taxonomy === 'equipment-rent') {
      breadcrumbs.push({
        label: 'For Rent',
        href: '/products?taxonomy=equipment-rent'
      });
    }

    // Add category breadcrumb from attributes
    // First try from product.categories (if available)
    if (product.categories && product.categories.length > 0) {
      const mainCategory = product.categories[0];
      breadcrumbs.push({
        label: mainCategory.name,
        href: `/products?taxonomy=${product.taxonomy || 'equipment-buy'}&product_cat=${mainCategory.slug}`
      });
    }
    // Otherwise try from attributes.pa_equipment-type
    else if (product.attributes && product.attributes['pa_categories'] && product.attributes['pa_categories'].length > 0) {
      const categoryName = Array.isArray(product.attributes['pa_categories'])
        ? product.attributes['pa_categories'][0]
        : product.attributes['pa_categories'];

      // Convert category name to slug format
      const categorySlug = categoryName.toLowerCase().replace(/\s+/g, '-');
      breadcrumbs.push({
        label: categoryName,
        href: `/products?taxonomy=${product.taxonomy || 'equipment-buy'}&term=${categorySlug}&termName=${categorySlug}`
      });
    }

    // Add current product (non-clickable)
    breadcrumbs.push({
      label: product.name,
      href: null,
      current: true
    });

    return breadcrumbs;
  };

  const breadcrumbs = buildBreadcrumbs();

  return (<>
      <ProductTextIconSlider bgColor={"rgba(0, 124, 145, 1)"}/>
    <div className="productPage min-h-screen bg-white py-4 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Dynamic Breadcrumb */}
        <nav className="hidden sm:flex items-center text-sm text-neutral-600 mb-2">
          {breadcrumbs.map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 && <ChevronRight className="w-4 h-4 inline-block mx-1" />}
              {item.current ? (
                <span className="text-neutral-900 truncate max-w-md" title={item.label}>
                  {item.label}
                </span>
              ) : (
                <Link
                  to={item.href}
                  className="hover:text-[#FFD200] inline-flex items-center"
                >
                  {item.icon && <item.icon className="w-4 h-4 mr-1" />}
                  {item.label}
                </Link>
              )}
            </React.Fragment>
          ))}
        </nav>
        {/* Product Details */}
        <div className="bg-white rounded-[4px] overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 sm:gap-10">
            {/* Image Gallery */}
            <div className="p-4">
              <div className="relative">
                {product.attributes.pa_condition && (
                  <span className="absolute top-4 left-4 z-10 px-3 py-[4px] text-[16px] font-semibold rounded-[40px]" style={{background: 'linear-gradient(159.21deg, #FFD200 11.19%, #F7971E 101.23%)', color:'rgba(26, 26, 26, 1)'
}}>
                    {decodeHTML(product.attributes.pa_condition)}
                  </span>
                )}

                <div className="aspect-auto bg-neutral-100 rounded-[4px] overflow-hidden">
                  <img
                    src={imageUrl}
                    alt={product.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Thumbnail Gallery Slider */}
              {images.length > 1 && (
                <div className="mt-4">
                  <div className="relative">
                    {/* Navigation buttons */}
                    {totalSlides > 1 && (
                      <>
                        <button
                          onClick={prevSlide}
                          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-shadow"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={nextSlide}
                          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-shadow"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </>
                    )}

                    {/* Thumbnail container with overflow hidden */}
                    <div className="overflow-hidden px-8">
                      <div
                        className="flex transition-transform duration-300 ease-in-out gap-2"
                        style={{
                          transform: `translateX(-${currentSlideIndex * 100}%)`,
                        }}
                      >
                        {Array.from(
                          { length: totalSlides },
                          (_, slideIndex) => (
                            <div
                              key={slideIndex}
                              className="flex-shrink-0 w-full grid grid-cols-4 gap-2"
                            >
                              {images
                                .slice(
                                  slideIndex * imagesPerSlide,
                                  (slideIndex + 1) * imagesPerSlide
                                )
                                .map((img, imgIndex) => {
                                  const globalIndex =
                                    slideIndex * imagesPerSlide + imgIndex;
                                  return (
                                    <button
                                      key={globalIndex}
                                      onClick={() =>
                                        setSelectedImage(globalIndex)
                                      }
                                      className={`aspect-square rounded-[4px] overflow-hidden border-2 transition-all hover:scale-90 ${
                                        selectedImage === globalIndex
                                          ? "border-[#1A1A1A] shadow-lg"
                                          : "border-neutral-200 hover:border-[#1A1A1A]"
                                      }`}
                                    >
                                      <img
                                        src={img.src || img.url}
                                        alt={`${product.name} ${
                                          globalIndex + 1
                                        }`}
                                        className="w-full h-full object-cover"
                                      />
                                    </button>
                                  );
                                })}
                            </div>
                          )
                        )}
                      </div>
                    </div>

                    {/* Slide indicators */}
                    {totalSlides > 1 && (
                      <div className="flex justify-center mt-3 gap-2">
                        {Array.from({ length: totalSlides }, (_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentSlideIndex(index)}
                            className={`w-2 h-2 rounded-full transition-colors ${
                              currentSlideIndex === index
                                ? "bg-slate-600"
                                : "bg-neutral-300 hover:bg-neutral-400"
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="p-4 lg:p-6">
              {/* Taxonomy Badge */}
             <div className="mb-4 flex items-center gap-6">
  {product.taxonomy === "equipment-buy" ? (
    <span
      className="inline-flex items-center px-4 py-[8px] text-sm font-semibold rounded-[40px]"
      style={{
        background: "rgba(212, 217, 231, 1)",
        color: "rgba(26, 26, 26, 1)",
      }}
    >
      For Sale
    </span>
  ) : product.taxonomy === "equipment-rent" ? (
    <span
      className="inline-flex items-center px-4 py-[8px] text-sm font-semibold rounded-[40px]"
      style={{
        background: "rgba(212, 217, 231, 1)",
        color: "rgba(26, 26, 26, 1)",
      }}
    >
      For Rent
    </span>
  ) : null}

  <span
    className="inline-flex items-center px-4 py-[8px] text-sm font-semibold rounded-[40px]"
    style={
      product.stock_status === "instock"
        ? {
            background: "rgba(212, 217, 231, 1)",
            color: "rgba(26, 26, 26, 1)",
          }
        : {
            backgroundColor: "#fee2e2",
            color: "#991b1b", 
          }
    }
  >
    {product.stock_status === "instock" ? "In Stock" : "Out of Stock"}
  </span>
</div>


              {/* Product Name */}
              
              <h1 className="text-4xl text-black my-4">{product.name}</h1>
              
              {/* Year of Manufacture - Extract from Attributes */}
                      {(() => {
                        let year = null;
                        // Extract year from pa_year-of-manufacture attribute
                        if (product.attributes && 
                            product.attributes['pa_year-of-manufacture'] && 
                            Array.isArray(product.attributes['pa_year-of-manufacture']) &&
                            product.attributes['pa_year-of-manufacture'].length > 0) {
                          year = product.attributes['pa_year-of-manufacture'][0];
                        }

                        // Validate year exists and is not empty
                        if (!year || year.toString().trim() === '') {
                          return null;
                        }
                        // Clean the year value
                        const cleanYear = year.toString().trim();

                        // If the year exists, render it
                        return (
                          <div className="my-5 flex items-center text-base font-bold text-neutral-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span>Year: {cleanYear}</span>
                          </div>
                        );
                      })()}

{/* Product Brand Logos */}
{product?.acf_fields?.brand_logo && (
  <>
  <p className="text-base text-black font-semibold mb-3">Available Brand</p>
  <div className="border-2 border-[transparent] hover:border-[#f90] hover:bg-[#fffaeb] rounded-lg p-2 sm:p-2 mb-5 flex items-center justify-center bg-white shadow-soft hover:shadow-elegant transition-all duration-300 w-full max-w-[120px] sm:max-w-[130px] md:max-w-[150px] lg:max-w-[150px]">
    <img
      src={product.acf_fields.brand_logo.url}
      alt={product.acf_fields.brand_logo.alt || `${product.title} brand logo`}
      className="w-full h-auto min-h-[80px] object-contain"
      loading="lazy"
    />
  </div>
  </>
)}
              {/* Short Description */}
              {product.short_description && (
                <div className="product_description mb-4">
                  <p
                    className="font-sm text-neutral-600"
                    dangerouslySetInnerHTML={{
                      __html: product.short_description,
                    }}
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="product-action-buttons flex flex-col sm:flex-row gap-4 mb-4 w-full">
                <button
                                className="gradient-btn flex-1 min-w-0"
                                onClick={handleOpenPopup}
                              >
                                <div className="gradient-btn-content">
                                  <Phone className="w-5 h-5 mr-2 flex-shrink-0" />
                                  <span>Request a Quote</span>
                                </div>
                              </button>

                              <div className="flex-1 min-w-0">
                              <WhatsAppButton/>
                              </div>
              </div>

              {/* Features */}
              <div className="border-t pt-6">
                <h3 className="font-display font-semibold mb-4">
                  Features & Benefits
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3 bg-white py-2 px-4 border border-neutral-200 shadow-soft hover:shadow-elegant transition-all duration-300 group hover:-translate-y-1">
                    <Truck className="w-5 h-5 text-neutral-600" />
                    <div>
                      <p className="font-medium">Fast Delivery</p>
                      <p className="text-sm text-neutral-600">
                        Ships within a week
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white py-2 px-4 border border-neutral-200 shadow-soft hover:shadow-elegant transition-all duration-300 group hover:-translate-y-1">
                    <Shield className="w-5 h-5 text-neutral-600" />
                    <div>
                      <p className="font-medium">Extended Warranty</p>
                      <p className="text-sm text-neutral-600">
                        1 + 3 Year for limited time
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white py-2 px-4 border border-neutral-200 shadow-soft hover:shadow-elegant transition-all duration-300 group hover:-translate-y-1">
                    <Clock className="w-5 h-5 text-neutral-600" />
                    <div>
                      <p className="font-medium">Help & Support</p>
                      <p className="text-sm text-neutral-600">
                        24/7 support for choosing right machine
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section Navigation */}
        <div
          className="sticky z-20 bg-white/95 backdrop-blur-sm border-b border-neutral-200 shadow-sm my-8"
          style={{ top: `${headerHeight}px` }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-6">
            <nav className="flex space-x-1 overflow-x-auto py-4">
              {navItems.map((item) => {
                const IconComponent = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all duration-200 whitespace-nowrap relative group ${
                      isActive
                        ? "text-black bg-white rounded-lg"
                        : "text-neutral-600 hover:text-slate-600"
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {item.label}
                    {/* Underline effect */}
                    <span
                      className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 h-0.5 bg-[#FFD200] transition-all duration-200 ${
                        isActive ? "w-[90%]" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Technical Specifications */}
        {product.acf_fields?.technical_specification?.length > 0 && (
          <section
            id="technical"
            className="bg-white rounded-xl border border-neutral-200 shadow-sm mb-16"
          >
            <div className="px-6 lg:px-8 py-16">
              <div className="flex items-center gap-3 mb-6">
                <Settings className="w-6 h-6 font-display font-bold text-black" />
                <h2 className="text-4xl font-normal text-black">
                  Technical <span className="font-semibold">Specifications</span>
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-neutral-200 rounded-lg overflow-hidden">
                  
                  <tbody className="divide-y divide-neutral-200">
                    <tr
                          className="hover:bg-neutral-50 transition-colors odd:bg-white even:bg-[#D4D9E7]"
                        >
                          <td className="px-6 py-4 text-sm text-neutral-900">
                            Reference Number
                          </td>
                          <td className="px-6 py-4 text-sm font-semibold text-neutral-600">
                            GS{product.id}
                          </td>
                        </tr>
                    {product.acf_fields.technical_specification.map(
                      (item, index) => (
                        <tr
                          key={index}
                          className="hover:bg-neutral-50 transition-colors odd:bg-white even:bg-[#D4D9E7]"
                        >
                          <td className="px-6 py-4 text-sm text-neutral-900 sm:w-[40%]">
                            {item.technical_name}
                          </td>
                          <td className="px-6 py-4 text-sm font-semibold  text-neutral-600 sm:w-[60%]">
                            {item.technical_value}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {product.acf_fields?.technical_specification_table && (
          <section
            id="technical"
            className="bg-white rounded-xl border border-neutral-200 shadow-sm mb-16"
          >
            <div className="px-6 lg:px-8 py-16">
              <div className="flex items-center gap-3 mb-6">
                <Settings className="w-6 h-6 font-display font-bold text-black" />
                <h2 className="text-4xl font-normal text-black">
                  Technical <span className="font-semibold">Specifications</span>
                </h2>
              </div>
          <div
                className="overflow-x-auto text-sm technical_specification_table"
                dangerouslySetInnerHTML={{
                  __html: product.acf_fields.technical_specification_table,
                }}
              />
        </div>
        </section>
        )}
        {/* Product Description */}
        {product.description && (
          <section
            id="product_description"
            className="bg-white rounded-xl border border-neutral-200 shadow-sm mb-16"
          >
            <div className="px-6 lg:px-8 py-16">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="w-6 h-6 font-display font-bold text-black" />
                <h2 className="text-4xl font-normal text-black">
                  Product <span className="font-semibold">Description</span>
                </h2>
              </div>
              <div
                className="prose max-w-none text-neutral-600 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            </div>
          </section>
        )}

        
        <section
            id="technical"
            className="bg-white rounded-xl border border-neutral-200 shadow-sm mb-16"
          >
            <div className="px-6 lg:px-8 py-16">
        <GetAQuoteEnquiryForm title={<h2 className="section-title">Get a <span className="bold">Quote</span></h2>} />
          </div>
          </section>


        {/* Features */}
        {/* {product.acf_fields?.features && (
          <section
            id="features"
            className="bg-white rounded-xl border border-neutral-200 shadow-sm mb-16"
          >
            <div className="p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-6">
                <Package2 className="w-6 h-6 font-display font-bold text-neutral-900" />
                <h2 className="text-2xl font-display font-bold text-neutral-900">
                  Features
                </h2>
              </div>
              <div
                className="prose max-w-none text-neutral-600 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: product.acf_fields.features,
                }}
              />
            </div>
          </section>
        )} */}

        {/* Payment Methods */}
        <section
          id="payment"
          className="bg-white rounded-xl border border-neutral-200 shadow-sm mb-16"
        >
          <div className="px-6 lg:px-8 py-16">
            <div className="flex flex-col gap-3 mb-6">
              <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 font-display font-bold text-black" />
              <h2 className="text-4xl font-normal text-black">
                Flexible & Secure <span className="font-semibold">Payment Options</span>
              </h2>
              </div>
              <p className="text-lg text-neutral-600">
              From cards to bank transfers, choose the option that works for you. Safe, simple, and always secure.
            </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Credit/Debit Cards */}
                        <div className="bg-white p-8 border border-neutral-200 shadow-soft hover:shadow-elegant transition-all duration-300 group hover:-translate-y-1">
                          <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                              <CreditCard className="w-8 h-8 text-accent-500" />
                            </div>
                            <div className="text-right">
                              <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                                Instant
                              </span>
                            </div>
                          </div>
                          <h6 className="text-xl font-semibold text-neutral-900 mb-3">
                            Credit/Debit Cards
                          </h6>
                          <p className="text-base text-neutral-600 mb-4">
                            Visa, Mastercard, American Express
                          </p>
                          <div className="mt-4 flex">
                            <img
                              src={PaymentIcons}
                              alt="Accepted payment cards"
                              className="w-20 h-12 object-contain"
                            />
                          </div>
                        </div>
            
                        {/* Manual Transfer */}
                        <div className="bg-white p-8 border border-neutral-200 shadow-soft hover:shadow-elegant transition-all duration-300 group hover:-translate-y-1">
                          <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                              <Banknote className="w-8 h-8 text-purple-600" />
                            </div>
                            <div className="text-right">
                              <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
                                1-2 Days
                              </span>
                            </div>
                          </div>
                          <h6 className="text-xl font-semibold text-neutral-900 mb-3">
                            Bank Transfer
                          </h6>
                          <p className="text-base text-neutral-600">
                            Direct bank deposit with detailed instructions
                          </p>
                          <div className="mt-4 flex">
                            <img
                              src={BankTransfer}
                              alt="Bank transfer"
                              className="w-20 h-12 object-contain"
                            />
                          </div>
                        </div>
            
                        {/* Buy Now, Pay Later */}
                        <div className="bg-white p-8 border border-neutral-200 shadow-soft hover:shadow-elegant transition-all duration-300 group hover:-translate-y-1 relative overflow-hidden">
                          <div className="absolute top-0 right-0 bg-gradient-to-l from-accent-500 to-accent-400 text-white px-3 py-1 text-xs font-medium">
                            Most Trusted
                          </div>
                          <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-colors">
                              <CalendarClock className="w-8 h-8 text-orange-600" />
                            </div>
                            <div className="text-right">
                              <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                                0% Interest
                              </span>
                            </div>
                          </div>
                          <h6 className="text-xl font-semibold text-neutral-900 mb-3">
                            Buy Now, Pay Later
                          </h6>
                          <p className="text-base text-neutral-600">
                            4 easy installments, 0% interest
                          </p>
                          <div className="mt-4 flex">
                            <img
                              src={BuyNowPayLater}
                              alt="Buy now pay later"
                              className="w-20 h-12 object-contain"
                            />
                          </div>
                        </div>
            
                        {/* Wire Transfer */}
                        <div className="bg-white p-8 border border-neutral-200 shadow-soft hover:shadow-elegant transition-all duration-300 group hover:-translate-y-1">
                          <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-indigo-100 rounded-lg group-hover:bg-indigo-200 transition-colors">
                              <Zap className="w-8 h-8 text-indigo-600" />
                            </div>
                            <div className="text-right">
                              <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                Secure
                              </span>
                            </div>
                          </div>
                          <h6 className="text-xl font-semibold text-neutral-900 mb-3">
                            Wire Transfer
                          </h6>
                          <p className="text-base text-neutral-600">
                            Fast & secure bank-to-bank
                          </p>
                          <div className="mt-4 flex">
                            <img
                              src={WireTransfer}
                              alt="Wire transfer"
                              className="w-20 h-12 object-contain"
                            />
                          </div>
                        </div>
                      </div>
            
                      {/* Trust Indicators */}
                      <div className="mt-12 text-center">
                        <div className="flex items-center justify-center gap-16 flex-wrap">
                          <div className="flex items-center gap-2 text-lg text-neutral-600">
                            <Shield className="w-6 h-6 text-green-600" />
                            <span>SSL Encrypted</span>
                          </div>
                          <div className="flex items-center gap-2 text-lg text-neutral-600">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                            <span>PCI Compliant</span>
                          </div>
                          <div className="flex items-center gap-2 text-lg text-neutral-600">
                            <Shield className="w-6 h-6 text-green-600" />
                            <span>100% Secure</span>
                          </div>
                        </div>
            
                        
                      </div>
          </div>
        </section>

        {/* Related Products Section */}
        {product && product.related_ids && product.related_ids.length > 0 && (
          <section
            id="related_products"
            className="bg-neutral-50 rounded-xl border border-neutral-200 shadow-sm mb-16"
          >
            <div className="px-6 lg:px-8 py-16">
              
              <RelatedProducts
                productIds={product.related_ids}
                currentProductId={productId}
                taxonomy={product.taxonomy || "equipment-buy"}
              />
              <div className="flex justify-center mt-4 items-center">
             <Link className="hidden font-semibold mt-4 sm:recommended-product-card__btn--secondary" to="/products?taxonomy=equipment-buy">
              View more Listings
            </Link>
          </div>
            </div>
            
          </section>
        )}
      </div>

      {/* EnquiryPopup */}
      <EnquiryPopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        product={product}
      />
    </div>
    <style jsx>{`
    .productPage .section-title{
    font-size: clamp(32px, 5vw, 36px)!important;
    }

    .productPage .enquiry-section{
      margin:0 auto!important;
      padding:0!important;
    }

    /* Product Action Buttons - Scoped to ProductDetailPage only */
    .productPage .product-action-buttons .gradient-btn-content{
      border-radius: 4px!important;
      padding: 14px 24px!important;
      white-space: nowrap;
    }

    .productPage .product-action-buttons .btn{
      padding: 14px 24px!important;
      border-radius: 4px!important;
      white-space: nowrap;
      width: 100%;
    }

    /* Mobile responsiveness for action buttons */
    @media screen and (max-width: 640px) {
      .productPage .product-action-buttons .gradient-btn-content{
        padding: 12px 16px!important;
        font-size: 14px;
      }

      .productPage .product-action-buttons .btn{
        padding: 12px 16px!important;
        font-size: 14px;
      }

      .productPage .product-action-buttons .gradient-btn-content span,
      .productPage .product-action-buttons .btn {
        white-space: normal;
        text-align: center;
      }
    }

    /* Tablet responsiveness */
    @media screen and (min-width: 641px) and (max-width: 1023px) {
      .productPage .product-action-buttons .gradient-btn-content{
        padding: 12px 20px!important;
        font-size: 15px;
      }

      .productPage .product-action-buttons .btn{
        padding: 12px 20px!important;
        font-size: 15px;
      }
    }

    .productPage .form-container{
      box-shadow: 0px 0px 35px 0px rgba(60, 60, 60, 0.2);
    }

    .productPage .feature-text{
      font-size:18px;
    }

    .technical_specification_table table{
      width:100%;
      margin-bottom:15px;
    }

    .technical_specification_table p{
      margin-bottom:10px;
    }

    .technical_specification_table p:last-child{
      margin-bottom:0px;
    }

    .technical_specification_table table td,
    .technical_specification_table table th {
    padding: 5px 10px;
    border: 1px solid #666;
    width: auto !important;
    height: auto !important;
    vertical-align: middle;
}

.technical_specification_table table td {
font-size: 14px;
padding: 5px 6px;
    }

    .technical_specification_table table tr td:nth-child(2) {
 white-space: nowrap;
    }

.technical_specification_table table tr:nth-child(even) {
  background-color: #f2f2f2;
}

/* Mobile responsiveness for technical specification table */
@media screen and (max-width: 640px) {
  .technical_specification_table table tr td:nth-child(2) {
    white-space: normal;
  }

  .technical_specification_table table td {
    font-size: 12px;
    padding: 4px 6px;
  }
}

.productPage .recommended-product-card__image-wrapper{
            padding:0!important;
            min-height: 240px;
            max-height: 240px;
}

.productPage .recommended-product-card__image-wrapper img {
    min-height: 100%;
    max-height: 100%;
    object-fit: contain;
    display:block;
}

.productPage .recommended-product-card__badges{
    bottom: .875rem!important;
}

@media screen and (max-width:640px){
.productPage .listingBtn{
      display:none!important;
}

}
 

    `}</style>
    </>
  );
};

export default ProductDetailPage;
